import { Size } from "./types";
import { Product, Variant } from "../../types/types";
import { getSizeReductionLabelInfo } from "../../utils/product/getSizeReductionLabelInfo";
import { getVariantDimensions } from "../../utils/product/getVariantDimensions";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { createSelectedSizeUrlObj } from "./utils/createSelectedSizeUrl";
import { useMemo } from "react";
import { useRouter } from "next/router";
import { getSizeQuery } from "../../utils/routing/getSizeQuery";
import { useVariant } from "../../hooks/useVariant/useVariant";

export const useAvailableSizes = (
  product: Product,
  options: { sortSizes: boolean },
  { showPromoOnIndicators }: { showPromoOnIndicators: boolean },
): Size[] => {
  const router = useRouter();
  const sizeParam = getSizeQuery(router.query);
  const { variant: selectedVariant } = useVariant(product);
  const selectedColour = selectedVariant?.differentiators?.colour;

  return useMemo(() => {
    const productVariants: Variant[] = product.variants;
    const sizesList = productVariants
      .filter((variant) =>
        selectedColour
          ? variant.differentiators?.size &&
            variant.differentiators.colour === selectedColour
          : variant.differentiators?.size,
      )
      .map((sizeVariant, _, sizeVariants) => {
        const isUnavailable =
          !sizeVariant.availability?.availableToOrder &&
          !isInStoreOnly(sizeVariant);
        const isOutOfStock =
          isUnavailable && !sizeVariant.availability?.futureAvailability;
        const { showReduced, label } = getSizeReductionLabelInfo(
          product,
          sizeVariant,
          { showPromoOnIndicators },
          isOutOfStock,
        );

        return {
          id: sizeVariant.id,
          name: sizeVariant.differentiators?.size || "",
          sublabel: getVariantDimensions(sizeVariant),
          sizeReductionLabel: label,
          showReduced,
          unavailable: isUnavailable,
          isOutOfStock,
          isSelected: isSelected(sizeVariant, sizeParam, sizeVariants.length),
          sizeHeadline: sizeVariant.differentiators?.sizeHeadline || "",
          url: createSelectedSizeUrlObj(sizeVariant, router.query),
        };
      });

    return options.sortSizes
      ? sortSizes(sizesList, isWithinBabyAndToddlerCategories(product))
      : sizesList;
  }, [
    product,
    router.query,
    sizeParam,
    selectedColour,
    options.sortSizes,
    showPromoOnIndicators,
  ]);
};

export const isSelected = (
  sizeVariant: Variant,
  selectedSize: string | null | undefined,
  sizeOptionsLength: number,
) => {
  const isSingleVariant = sizeOptionsLength === 1;

  if (isSingleVariant) {
    return true;
  }

  if (selectedSize) {
    return sizeVariant.pdpURL.query?.size === selectedSize;
  }

  return false;
};

export const sortSizes = (sizes: Size[], isBabyAndToddler: boolean) => {
  const nonNumericSizes = sizes.filter(({ name }) => isNaN(parseInt(name)));
  const numericSizes = sizes
    .filter(({ name }) => !isNaN(parseInt(name)))
    .sort((a, b) => parseInt(a.name) - parseInt(b.name));
  const sizesSorted = [...nonNumericSizes, ...numericSizes];

  if (isBabyAndToddler) {
    const sizesPremAndOther = sizesSorted.filter(
      (sortedSize) =>
        !sortedSize.name.toLowerCase().includes("tiny baby") &&
        !sortedSize.name.toLowerCase().includes("newborn") &&
        !sortedSize.name.includes("month") &&
        !sortedSize.name.includes("year"),
    );

    const sizeTinyBaby = sizesSorted.filter((sortedSize) =>
      sortedSize.name.toLowerCase().includes("tiny baby"),
    );

    const sizeNewborn = sizesSorted.filter((sortedSize) =>
      sortedSize.name.toLowerCase().includes("newborn"),
    );

    const sizesInMonths = sizesSorted.filter((size) =>
      size.name.includes("month"),
    );

    const sizesInYears = sizesSorted.filter((size) =>
      size.name.includes("year"),
    );

    return [
      ...sizesPremAndOther,
      ...sizeTinyBaby,
      ...sizeNewborn,
      ...sizesInMonths,
      ...sizesInYears,
    ];
  }

  return sizesSorted;
};

export const isWithinBabyAndToddlerCategories = (product: Product) => {
  return product.browsePageHierarchy.some((category) =>
    "url" in category
      ? category.url === "/baby-child/baby-toddlerwear-0-3-yrs/c6000030"
      : false,
  );
};
